/**
 * #17 (J15) — segment-breakdown discipline for the "Break down by X" views.
 *
 * Live finding: age bands rendered in arbitrary (size-desc) order — 25-34, 45-54,
 * 55-64, 18-24, 35-44 — with no n per cell and rainbow category colors on an ordinal
 * axis. This module owns the pure contracts the breakdown renderers share:
 *
 *   - `orderSegmentBands(labels)` — ordinal-aware band ordering. Survey-agnostic: it
 *     PARSES numbers out of the labels (age ranges by lower bound, household sizes /
 *     counts numerically, "Under X" before X, "X+" tails by X); labels with no number
 *     fall back to alphabetical. No hardcoded band lists.
 *   - `breakdownRows(cut)` — reorders per-band rows ({label, value, n}) into that
 *     band order, so every renderer shows the same disciplined sequence and carries
 *     the cell base (n) it must display per row.
 *   - `THIN_CELL_MIN` / `hasThinCells` / `THIN_CELL_CAVEAT` — the thin-cell guard: any
 *     band under 8 twins gets the blanket small-groups caveat line.
 *
 * Pure + dependency-free so it unit-tests without React.
 */

/** One band's row in a segment breakdown: display label, headline value, cell base. */
export interface BreakdownRow {
  label: string
  value: number
  n: number
}

/** Bands with fewer than this many twins are "thin" — directional only. */
export const THIN_CELL_MIN = 8

/** The blanket caveat line rendered under a breakdown containing any thin cell.
 *  Result-specific honesty, not a self-disparaging disclaimer (no "directional" framing
 *  per the wording rule) — it states the concrete limit: tiny groups can flip on a twin. */
export const THIN_CELL_CAVEAT = `Small groups — at least one group has fewer than ${THIN_CELL_MIN} twins, so a couple of twins can swing its numbers.`

// The ordinal bound of a band label, or null when the label carries no number.
// "18-24" → 18 · "65+" → 65 · "3-4 people" → 3 · "$100–$199" → 100 · "Under 18" → 17.5
// (just below 18, so it sorts BEFORE the "18-24" band) · "female" → null.
function bandBound(label: string): number | null {
  const text = String(label)
  const match = text.match(/-?\d+(?:[.,]\d+)?/)
  if (!match) return null
  let bound = Number(match[0].replace(',', '.'))
  if (!Number.isFinite(bound)) return null
  // "Under 18" / "less than 3" / "<18" mean everything BELOW the number, so the band
  // belongs just before the band that STARTS at that number.
  if (/(?:\bunder\b|\bless\s+than\b|\bbelow\b|<)/i.test(text)) bound -= 0.5
  return bound
}

/**
 * Order segment-band labels for display. Labels that parse an ordinal bound sort by it
 * ascending (ties break on a numeric-aware label compare, so "18-21" precedes "18-24");
 * labels with no number sort alphabetically AFTER the ordinal ones (e.g. a "Prefer not
 * to say" tail stays at the end of an age-band list). An all-non-numeric set is plain
 * alphabetical. Pure — returns a new array.
 */
export function orderSegmentBands(labels: ReadonlyArray<string>): Array<string> {
  const ordinal: Array<{ label: string; bound: number }> = []
  const nominal: Array<string> = []
  for (const label of labels) {
    const bound = bandBound(label)
    if (bound == null) nominal.push(label)
    else ordinal.push({ label, bound })
  }
  ordinal.sort(
    (a, b) => a.bound - b.bound || a.label.localeCompare(b.label, undefined, { numeric: true }),
  )
  nominal.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  return [...ordinal.map((o) => o.label), ...nominal]
}

/**
 * Reorder a breakdown cut's rows into display band order (see orderSegmentBands),
 * preserving each row's {label, value, n} payload (and any extra fields the caller
 * rides along, e.g. the band's raw response slice). Duplicate labels keep their
 * relative order. Pure — returns a new array.
 */
export function breakdownRows<T extends { label: string }>(cut: ReadonlyArray<T>): Array<T> {
  const order = orderSegmentBands(cut.map((row) => row.label))
  const position = new Map<string, number>()
  order.forEach((label, index) => {
    if (!position.has(label)) position.set(label, index)
  })
  return [...cut].sort(
    (a, b) => (position.get(a.label) ?? 0) - (position.get(b.label) ?? 0),
  )
}

/** True when any band in the cut is thin (0 < n < THIN_CELL_MIN) — show the caveat. */
export function hasThinCells(rows: ReadonlyArray<{ n: number }>): boolean {
  return rows.some((row) => row.n > 0 && row.n < THIN_CELL_MIN)
}

// ── Segmented-treatment mode (Step 2 resolution: adaptive, two modes) ─────────
// The Compare-segments exhibit renders one of two ways:
//   - 'rows'   (Mode A) — every selected cut stacked as a band under the "Everyone"
//                anchor. Used while the segmented view stays compact.
//   - 'toggle' (Mode B) — one cut shown at a time behind a selector. Used when Mode A
//                would run too tall (many cuts × many rows), or the shape can't stack.
// The gate is TOTAL rendered rows: (selected cuts + 1 anchor) × the per-band row count.
// Past SEGMENT_TOGGLE_ROW_MAX the exhibit switches to the toggle so a card never grows
// into a wall of small multiples. rowCount matters — a 6-item monadic rating × 4 cuts
// (≈30 rows with the anchor) is NOT a 2-option pie × 4 cuts (≈10).
export const SEGMENT_TOGGLE_ROW_MAX = 18

// Shapes that can never stack as rows (matrix cells, radials, price curves, migration
// grids, the scorecard heatmap) are ALWAYS toggle when segmented.
const NON_STACKABLE_PLOTS = new Set<string>([
  'matrix', 'heatmap', 'radar', 'psm', 'quadrant', 'migration_matrix', 'scorecard',
])

export function segmentedMode(plot: string, cutCount: number, rowCount: number): 'rows' | 'toggle' {
  if (NON_STACKABLE_PLOTS.has(plot)) return 'toggle'
  const anchoredRows = (cutCount + 1) * Math.max(1, rowCount)
  return anchoredRows > SEGMENT_TOGGLE_ROW_MAX ? 'toggle' : 'rows'
}

// ── Which plots support client-side segment comparison at all ────────────────
// Recomputability is a DECLARED flag (crafted Wave 0): a plot supports segments only when its
// numbers can be re-derived client-side from the cut's raw responses. Two families can't:
//   - Pricing charts (psm curve, migration matrix) render BACKEND aggregates with no per-cut
//     source (Step 4B-i, RESOLVED: skipped).
//   - Theme/codeframe views (themes, its heatmap/bars sub-views, word cloud) render python's
//     PRECOMPUTED cluster percentages off the full base — a selected cut has no client-side
//     recompute path for them (unlike the raw-quotes list, which slices correctly through the
//     separate `openEndQuoteResponses` channel and stays OUT of this set). Before this fix
//     `themes` was missing here, so it silently kept claiming segmentability while ignoring
//     the selected cuts — R1.1's honesty fix, a stated behavior change.
// Everything else response-derived supports segments. The gate covers the REROUTED/programmatic
// selection paths (a `segment_by` default or a "break down by X" chip) landing on an
// unsupported chart — the picker itself is hidden there, so no live click reaches this.
const UNSUPPORTED_SEGMENT_PLOTS = new Set<string>([
  'psm',
  'migration_matrix',
  'themes',
  'themes_heatmap',
  'themes_bars',
  'word_cloud',
  'wordcloud',
])

export function supportsSegments(plot: string): boolean {
  return !UNSUPPORTED_SEGMENT_PLOTS.has(plot)
}

/** The shared copy for the "not-yet-supported" alert line. */
export const SEGMENTS_UNAVAILABLE_NOTE = "Segment comparison isn't available for this chart yet."
